import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ToastProvider } from './components/ui/ToastContext';
import { ToastContainer } from './components/ui/Toast';
import { AuthProvider } from './context/AuthContext';
import { NetworkProvider } from './context/NetworkContext';
import { NetworkBanner } from './components/ui/NetworkBanner';
import { Layout } from './components/layout/Layout';
import { NotFoundPage } from './pages/public/NotFoundPage';

// Lazy load public pages
const LoginPage = lazy(() =>
  import('./pages/public/LoginPage').then((m) => ({ default: m.LoginPage })),
);

// Lazy load protected pages
const DashboardPage = lazy(() =>
  import('./pages/protected/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const MailboxesPage = lazy(() =>
  import('./pages/protected/MailboxesPage').then((m) => ({ default: m.MailboxesPage })),
);
const MailboxFormPage = lazy(() =>
  import('./pages/protected/MailboxFormPage').then((m) => ({ default: m.MailboxFormPage })),
);
const SyncLogPage = lazy(() =>
  import('./pages/protected/SyncLogPage').then((m) => ({ default: m.SyncLogPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/protected/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const BillingPage = lazy(() =>
  import('./pages/protected/BillingPage').then((m) => ({ default: m.BillingPage })),
);

// Lazy load admin pages
const AdminUsersPage = lazy(() =>
  import('./pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminWaitlistPage = lazy(() =>
  import('./pages/admin/AdminWaitlistPage').then((m) => ({ default: m.AdminWaitlistPage })),
);

function ErrorFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred. Please refresh the page.</p>
      <button type="button" onClick={() => window.location.reload()}>
        Refresh
      </button>
    </div>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <ToastProvider>
          <ToastContainer />
          <NetworkProvider>
            <Routes>
              {/* Public routes — no auth required */}
              <Route
                path="/"
                element={
                  <Suspense>
                    <LoginPage />
                  </Suspense>
                }
              />

              {/* Authenticated routes — wrapped in AuthProvider + Layout */}
              <Route
                element={
                  <AuthProvider>
                    <NetworkBanner />
                    <Layout />
                  </AuthProvider>
                }
              >
                <Route
                  path="/dashboard"
                  element={
                    <Suspense>
                      <DashboardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mailboxes"
                  element={
                    <Suspense>
                      <MailboxesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mailboxes/new"
                  element={
                    <Suspense>
                      <MailboxFormPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mailboxes/:id/edit"
                  element={
                    <Suspense>
                      <MailboxFormPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/sync-log"
                  element={
                    <Suspense>
                      <SyncLogPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Suspense>
                      <SettingsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <Suspense>
                      <BillingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <Suspense>
                      <AdminUsersPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/waitlist"
                  element={
                    <Suspense>
                      <AdminWaitlistPage />
                    </Suspense>
                  }
                />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </NetworkProvider>
        </ToastProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
